/**
 * Agency operations: the org chart, internal messages, court scheduling and evidence dispositions,
 * fleet and quartermaster equipment, inventory self-certification, and nonferrous metal (scrap) permits.
 * Pure rules and labels; the server stores and enforces them.
 */
import { addDaysIso } from './time.ts';

// ─── Org chart ────────────────────────────────────────────────────────────────

export const ORG_KINDS = ['division', 'shift', 'unit'] as const;
export type OrgKind = (typeof ORG_KINDS)[number];
export const ORG_KIND_LABEL: Record<OrgKind, string> = { division: 'Division', shift: 'Shift / team', unit: 'Unit' };

export interface OrgUnit {
  id: string;
  parent_id: string | null;
  supervisor_id: string | null;
}

/**
 * Who supervises a person: the supervisor of their unit — or, if they ARE that unit's supervisor
 * (or it has none), the next one up the chart. Null when nobody above them is set.
 */
export function supervisorOf(userId: string, unitId: string | null, units: OrgUnit[]): string | null {
  const byId = new Map(units.map((u) => [u.id, u]));
  let cur = unitId ? byId.get(unitId) : undefined;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    if (cur.supervisor_id && cur.supervisor_id !== userId) return cur.supervisor_id;
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
  }
  return null;
}

/** True when making `parentId` the parent of `unitId` would loop back on itself. */
export function wouldCycle(unitId: string, parentId: string | null, units: OrgUnit[]): boolean {
  const byId = new Map(units.map((u) => [u.id, u]));
  let cur = parentId;
  const seen = new Set<string>();
  while (cur) {
    if (cur === unitId || seen.has(cur)) return true;
    seen.add(cur);
    cur = byId.get(cur)?.parent_id ?? null;
  }
  return false;
}

/**
 * A shift's rotation: which days it works, repeating from an anchor date. `pattern` is one character
 * per day — "1" works, "0" off — e.g. a 14-day Pitman 2-2-3 is "11001110011000".
 */
export interface Rotation {
  anchor: string;
  pattern: string;
  start: string;
  end: string;
}

export const ROTATION_PRESETS: { key: string; label: string; pattern: string; start: string; end: string }[] = [
  { key: 'pitman', label: '12-hour Pitman (2-2-3)', pattern: '11001110011000', start: '07:00', end: '19:00' },
  { key: 'pitman_n', label: '12-hour Pitman nights', pattern: '11001110011000', start: '19:00', end: '07:00' },
  { key: 'panama', label: '12-hour 4 on / 4 off', pattern: '11110000', start: '07:00', end: '19:00' },
  { key: 'mf8', label: 'Monday–Friday, 8 hours', pattern: '1111100', start: '08:00', end: '17:00' },
];

export function checkRotation(r: Rotation): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(r.anchor)) return 'Rotation needs a start date';
  if (!/^[01]{1,56}$/.test(r.pattern)) return 'Pattern is 1–56 days of 1 (work) and 0 (off)';
  if (!/^\d{2}:\d{2}$/.test(r.start) || !/^\d{2}:\d{2}$/.test(r.end)) return 'Shift needs start and end times';
  return null;
}

const dayIndex = (iso: string) => Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86_400_000);

/** Does the shift work the tour that STARTS on `date`? */
export function worksOn(r: Rotation, date: string): boolean {
  const n = r.pattern.length;
  const i = (((dayIndex(date) - dayIndex(r.anchor)) % n) + n) % n;
  return r.pattern[i] === '1';
}

/** Is the shift on duty at local `date` `time`? Overnight tours (end ≤ start) count from the day they started. */
export function onDutyAt(r: Rotation, date: string, time: string): boolean {
  const overnight = r.end <= r.start;
  if (!overnight) return worksOn(r, date) && time >= r.start && time < r.end;
  if (time >= r.start) return worksOn(r, date);
  return time < r.end && worksOn(r, addDaysIso(date, -1));
}

// ─── Internal messages ────────────────────────────────────────────────────────

/** "Response required" windows the sender can pick (days). */
export const RESPOND_DAYS = [1, 2, 3, 5, 7] as const;
export const DEFAULT_RESPOND_DAYS = 3;

/** When a response is due: the same time of day, `days` later. */
export function respondBy(sentAt: Date, days: number): Date {
  return new Date(+sentAt + days * 86_400_000);
}

/** Overdue: a response was required, none came, and the deadline has passed. */
export function isOverdue(r: { respond_by: Date | string | null; responded_at: Date | string | null }, now: Date): boolean {
  return !!r.respond_by && !r.responded_at && +new Date(r.respond_by) < +now;
}

// ─── Court ────────────────────────────────────────────────────────────────────

export const COURT_EVENT_KINDS = ['trial', 'jury_trial', 'bench_trial', 'preliminary', 'bond', 'hearing', 'security'] as const;
export type CourtEventKind = (typeof COURT_EVENT_KINDS)[number];
export const COURT_EVENT_LABEL: Record<CourtEventKind, string> = {
  trial: 'Trial',
  jury_trial: 'Jury trial',
  bench_trial: 'Bench trial',
  preliminary: 'Preliminary hearing',
  bond: 'Bond hearing',
  hearing: 'Hearing',
  security: 'Court security only',
};

export const COURT_ROLES = ['security', 'testifying', 'transport', 'other'] as const;
export type CourtRole = (typeof COURT_ROLES)[number];
export const COURT_ROLE_LABEL: Record<CourtRole, string> = {
  security: 'Courtroom security',
  testifying: 'Testifying',
  transport: 'Prisoner transport',
  other: 'Other duty',
};

export const COURT_STATUS = ['scheduled', 'held', 'continued', 'cancelled'] as const;
export type CourtStatus = (typeof COURT_STATUS)[number];
export const COURT_STATUS_LABEL: Record<CourtStatus, string> = { scheduled: 'Scheduled', held: 'Held', continued: 'Continued', cancelled: 'Cancelled' };

/** What the court wants done with an item in evidence. */
export const DISPOSITION_DECISIONS = ['hold', 'release_owner', 'destroy', 'forfeit'] as const;
export type DispositionDecision = (typeof DISPOSITION_DECISIONS)[number];
export const DISPOSITION_LABEL: Record<DispositionDecision, string> = {
  hold: 'Keep in evidence',
  release_owner: 'Release to the owner',
  destroy: 'Destroy',
  forfeit: 'Forfeit to the agency',
};
export const DISPOSITION_HINT: Record<DispositionDecision, string> = {
  hold: 'Case still open, appeal pending, or needed for another case',
  release_owner: 'Give it back to its rightful owner',
  destroy: 'Drugs, contraband, weapons ordered destroyed',
  forfeit: 'Becomes agency property (court order)',
};

/** Evidence statuses that are still "in evidence" (can still be disposed of). */
export const OPEN_EVIDENCE = ['pending', 'in_custody', 'checked_out'] as const;

// ─── Fleet & quartermaster ────────────────────────────────────────────────────

export const FLEET_STATUS = ['assigned', 'spare', 'out_of_service', 'retired'] as const;
export type FleetStatus = (typeof FLEET_STATUS)[number];
export const FLEET_STATUS_LABEL: Record<FleetStatus, string> = { assigned: 'Assigned', spare: 'Spare', out_of_service: 'Out of service', retired: 'Retired' };

export const EQUIPMENT_CATEGORIES = [
  ['radio_mobile', 'Radio — in-car (mobile)'],
  ['radio_portable', 'Radio — portable'],
  ['radar', 'Radar'],
  ['lidar', 'Lidar'],
  ['camera_car', 'In-car camera'],
  ['camera_body', 'Body camera'],
  ['mdt', 'Laptop / MDT'],
  ['printer', 'Ticket printer'],
  ['lightbar', 'Light bar / siren'],
  ['handgun', 'Handgun'],
  ['rifle', 'Rifle'],
  ['shotgun', 'Shotgun'],
  ['taser', 'Taser / CEW'],
  ['vest', 'Body armor'],
  ['breath', 'Breath tester'],
  ['aed', 'AED / first aid'],
  ['keys', 'Keys / fob / card'],
  ['uniform', 'Uniform item'],
  ['phone', 'Phone'],
  ['other', 'Other'],
] as const;
export type EquipmentCategory = (typeof EQUIPMENT_CATEGORIES)[number][0];
export const EQUIPMENT_CATEGORY_LABEL = Object.fromEntries(EQUIPMENT_CATEGORIES) as Record<EquipmentCategory, string>;

export const EQUIPMENT_STATUS = ['in_stock', 'issued', 'installed', 'repair', 'retired', 'lost'] as const;
export type EquipmentStatus = (typeof EQUIPMENT_STATUS)[number];
export const EQUIPMENT_STATUS_LABEL: Record<EquipmentStatus, string> = {
  in_stock: 'In stock',
  issued: 'Issued to a person',
  installed: 'Installed in a vehicle',
  repair: 'Out for repair',
  retired: 'Retired / surplused',
  lost: 'Lost / stolen',
};

/** VIN: 17 characters, no I, O or Q, check digit (position 9) must match. Older (pre-1981) VINs are allowed when `strict` is false. */
export function checkVin(vin: string, strict = true): string | null {
  const v = vin.trim().toUpperCase();
  if (!v) return 'VIN is required';
  if (!strict && v.length < 17) return /^[A-Z0-9]{5,16}$/.test(v) ? null : 'VIN can only have letters and numbers';
  if (v.length !== 17) return 'VIN must be 17 characters';
  if (/[IOQ]/.test(v)) return 'VINs never use the letters I, O or Q';
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(v)) return 'VIN can only have letters and numbers';
  const T: Record<string, number> = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
  const W = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 17; i++) sum += (/\d/.test(v[i]!) ? Number(v[i]) : T[v[i]!]!) * W[i]!;
  const check = sum % 11 === 10 ? 'X' : String(sum % 11);
  return v[8] === check ? null : 'VIN check digit doesn’t match — check for a typo';
}

/** Inventory self-certification: how often deputies certify what they've been issued (days). */
export const DEFAULT_CERT_DAYS = 90;
export function certDue(lastCertifiedOn: string | null, everyDays: number, today: string): { due: string | null; overdue: boolean } {
  if (!lastCertifiedOn) return { due: null, overdue: true };
  const due = addDaysIso(lastCertifiedOn, everyDays);
  return { due, overdue: due < today };
}

// ─── Nonferrous metal (scrap) permits — S.C. Code § 16-17-680(C) ──────────────

export const PERMIT_STATUS = ['active', 'denied', 'suspended', 'revoked', 'replaced'] as const;
export type PermitStatus = (typeof PERMIT_STATUS)[number];
export const PERMIT_STATUS_LABEL: Record<PermitStatus | 'expired', string> = {
  active: 'Active',
  denied: 'Denied',
  suspended: 'Suspended',
  revoked: 'Revoked',
  replaced: 'Replaced',
  expired: 'Expired',
};
/** § 16-17-680(C)(5): no fee for the permit; up to $10 to replace a lost or destroyed one. */
export const PERMIT_FEE = 0;
export const REPLACEMENT_FEE = 10;

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const mmdd = (y: number, md: string) => (md === '02-29' && !isLeap(y) ? `${y}-02-28` : `${y}-${md}`);

/**
 * § 16-17-680(C)(8): an individual's permit expires on their birthday in the second calendar year
 * after the year it was issued; an entity's on the anniversary of the issue date in that year.
 * (A Feb 29 date falls on Feb 28 in a non-leap year.)
 */
export function permitExpires(kind: 'individual' | 'entity', issuedOn: string, dob: string | null): string {
  const y = Number(issuedOn.slice(0, 4)) + 2;
  if (kind === 'individual') {
    if (!dob || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) throw new Error('An individual permit needs a date of birth');
    return mmdd(y, dob.slice(5));
  }
  return mmdd(y, issuedOn.slice(5));
}

export function permitState(p: { status: PermitStatus; expires_on: string }, today: string): PermitStatus | 'expired' {
  return p.status === 'active' && p.expires_on < today ? 'expired' : p.status;
}

/** What the application must have before a permit can be issued. */
export interface PermitApplication {
  kind: 'individual' | 'entity';
  lastName: string;
  firstName: string;
  entityName?: string | null;
  dob: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  idNumber: string;
  idState: string;
  resident: boolean;
  declarationSigned: boolean;
  noDisqualifyingConviction: boolean;
  hasPhoto: boolean;
  hasIdCopy: boolean;
}

export function checkPermitApplication(a: PermitApplication): string[] {
  const p: string[] = [];
  if (a.kind === 'entity' && !a.entityName?.trim()) p.push('Business name is required for a business permit.');
  if (!a.lastName.trim() || !a.firstName.trim()) p.push(a.kind === 'entity' ? 'Name of the employee who will haul and sell is required.' : 'Applicant name is required.');
  if (a.kind === 'individual' && !a.dob) p.push('Date of birth is required (an individual’s permit expires on their birthday).');
  if (!a.address.trim() || !a.city.trim() || !a.state.trim() || !a.zip.trim()) p.push('Full address is required.');
  if (!a.idNumber.trim() || !a.idState.trim()) p.push('Driver’s license or ID number and state are required.');
  if (!a.hasIdCopy) p.push('A copy of the ID must be kept (§ 16-17-680(C)(7)).');
  if (!a.hasPhoto) p.push('A photograph of the holder must be kept (§ 16-17-680(C)(7)).');
  if (!a.declarationSigned) p.push('The applicant must sign the declaration that they know and will follow § 16-17-680.');
  if (!a.noDisqualifyingConviction) p.push('A person convicted under § 16-11-523 or § 16-17-680 can’t get a permit.');
  return p;
}

// ─── Case files for the solicitor ─────────────────────────────────────────────

export const CASE_OUTCOMES = ['pending', 'pled_guilty', 'trial_guilty', 'trial_not_guilty', 'dismissed', 'diversion', 'other'] as const;
export type CaseOutcome = (typeof CASE_OUTCOMES)[number];
export const CASE_OUTCOME_LABEL: Record<CaseOutcome, string> = {
  pending: 'Still pending',
  pled_guilty: 'Pled guilty',
  trial_guilty: 'Went to trial — guilty',
  trial_not_guilty: 'Went to trial — not guilty',
  dismissed: 'Dismissed / nolle prossed',
  diversion: 'PTI / diversion',
  other: 'Other',
};
/** Outcomes that end the case (evidence can be disposed of). */
export const FINAL_OUTCOMES: CaseOutcome[] = ['pled_guilty', 'trial_guilty', 'trial_not_guilty', 'dismissed', 'diversion'];
export const FOLLOWUP_DAYS = [30, 60, 90, 180] as const;
export const DEFAULT_FOLLOWUP_DAYS = 60;
/** When to ask "did this case go to trial?": after the wait, or the day after the last court date if that's later. */
export function followupDue(sharedAt: Date, days: number, lastCourtDate: Date | null): Date {
  const a = +sharedAt + days * 86_400_000;
  const b = lastCourtDate ? +lastCourtDate + 86_400_000 : 0;
  return new Date(Math.max(a, b));
}

// ─── Case follow-through (investigations) ─────────────────────────────────────

export const CASE_STATUS = ['active', 'suspended', 'cleared_arrest', 'cleared_exceptional', 'unfounded', 'referred', 'closed'] as const;
export type CaseStatus = (typeof CASE_STATUS)[number];
export const CASE_STATUS_LABEL: Record<CaseStatus, string> = {
  active: 'Active investigation',
  suspended: 'Suspended / inactive (no leads)',
  cleared_arrest: 'Cleared by arrest',
  cleared_exceptional: 'Exceptionally cleared',
  unfounded: 'Unfounded',
  referred: 'Referred to another agency',
  closed: 'Closed',
};

/** The big steps a case goes through, for the deputy's "where is my case" view. */
export const CASE_STAGES = ['written', 'approved', 'investigations', 'solicitor', 'court', 'done'] as const;
export type CaseStage = (typeof CASE_STAGES)[number];
export const CASE_STAGE_LABEL: Record<CaseStage, string> = {
  written: 'Written',
  approved: 'Approved',
  investigations: 'Investigations',
  solicitor: 'Solicitor',
  court: 'Court',
  done: 'Finished',
};

export function caseStage(c: {
  status: string;
  investigator: boolean;
  caseStatus: CaseStatus | null;
  sharedWithSolicitor: boolean;
  courtDates: number;
  outcome: CaseOutcome | null;
}): CaseStage {
  if (c.outcome && c.outcome !== 'pending') return 'done';
  if (c.caseStatus && ['unfounded', 'closed', 'referred', 'cleared_exceptional'].includes(c.caseStatus) && !c.sharedWithSolicitor) return 'done';
  if (c.courtDates) return 'court';
  if (c.sharedWithSolicitor) return 'solicitor';
  if (c.investigator || c.caseStatus) return 'investigations';
  if (c.status === 'approved') return 'approved';
  return 'written';
}

// ─── Case photos: tags, labels and folders ────────────────────────────────────

export const PHOTO_TAGS = ['person', 'property', 'evidence', 'scene', 'other'] as const;
export type PhotoTagKind = (typeof PHOTO_TAGS)[number];
export const PHOTO_TAG_LABEL: Record<PhotoTagKind, string> = { person: 'Person', property: 'Property', evidence: 'Evidence', scene: 'Scene', other: 'Other' };
export interface PhotoTag {
  kind: PhotoTagKind;
  /** Which person / property item / evidence item. */
  ref?: string | null;
  /** For "other": what it shows. */
  note?: string | null;
}

const ROLE_ORDER = ['victim', 'suspect', 'arrestee', 'witness', 'complainant', 'other'] as const;
const ROLE_WORD: Record<string, string> = { victim: 'Victim', suspect: 'Suspect', arrestee: 'Arrestee', witness: 'Witness', complainant: 'Complainant', other: 'Other person' };

/** "Victim 1", "Suspect 2", "Property 3" — how photos are labeled and foldered for a report. */
export function photoSubjects(r: { people: { id: string; roles: string[]; name: string; unknown?: boolean }[]; property: { id: string; description: string; type?: string; make?: string | null; model?: string | null }[] }) {
  const n: Record<string, number> = {};
  const people = r.people.map((p) => {
    const role = ROLE_ORDER.find((x) => p.roles.includes(x)) ?? 'other';
    n[role] = (n[role] ?? 0) + 1;
    return { id: p.id, label: `${ROLE_WORD[role]} ${n[role]}`, name: p.unknown || !p.name.trim() ? (role === 'suspect' ? 'Unknown suspect' : 'Name not known') : p.name.trim() };
  });
  const property = r.property.map((p, i) => ({ id: p.id, label: `Property ${i + 1}`, name: [p.description, p.make, p.model].filter(Boolean).join(' ') || 'Item' }));
  return { people, property };
}

/** The folders a photo goes in (one per tag). */
export function photoFolders(tags: PhotoTag[], subjects: ReturnType<typeof photoSubjects>, evidence: { id: string; number: string }[]): string[] {
  const out = tags.map((t) => {
    if (t.kind === 'person') return subjects.people.find((p) => p.id === t.ref)?.label ?? 'Person (removed from report)';
    if (t.kind === 'property') return subjects.property.find((p) => p.id === t.ref)?.label ?? 'Property (removed from report)';
    if (t.kind === 'evidence') return `Evidence ${evidence.find((e) => e.id === t.ref)?.number ?? ''}`.trim();
    return PHOTO_TAG_LABEL[t.kind];
  });
  return out.length ? [...new Set(out)] : ['Not labeled'];
}

/** A tag must say which person / item it is. */
export function checkPhotoTags(tags: PhotoTag[]): string | null {
  for (const t of tags) {
    if (!(PHOTO_TAGS as readonly string[]).includes(t.kind)) return 'Unknown photo label';
    if ((t.kind === 'person' || t.kind === 'property' || t.kind === 'evidence') && !t.ref) return `Pick which ${t.kind === 'person' ? 'person' : t.kind === 'property' ? 'property item' : 'evidence item'} the photo shows`;
    if (t.kind === 'other' && !t.note?.trim()) return 'Say what the “Other” photo shows';
  }
  return null;
}

// ─── Warrants ─────────────────────────────────────────────────────────────────

export const WARRANT_KINDS = ['arrest', 'bench', 'search', 'probation', 'family_bench', 'other'] as const;
export type WarrantKind = (typeof WARRANT_KINDS)[number];
export const WARRANT_KIND_LABEL: Record<WarrantKind, string> = {
  arrest: 'Arrest warrant',
  bench: 'Bench warrant',
  search: 'Search warrant',
  probation: 'Probation violation',
  family_bench: 'Family court bench warrant',
  other: 'Other',
};
/** What a deputy can ask a judge for. Bench, probation and family court warrants come from the court and are only entered by the warrants office. */
export const WARRANT_REQUEST_KINDS = ['arrest', 'search'] as const;
export const WARRANT_STATUS = ['active', 'served', 'recalled', 'cancelled'] as const;
export type WarrantStatus = (typeof WARRANT_STATUS)[number];
export const WARRANT_STATUS_LABEL: Record<WarrantStatus, string> = { active: 'Active', served: 'Served', recalled: 'Recalled', cancelled: 'Cancelled' };
export const WARRANT_CAUTIONS = ['armed', 'violent', 'flight', 'mental_health', 'officer_safety', 'medical'] as const;
export const WARRANT_CAUTION_LABEL: Record<(typeof WARRANT_CAUTIONS)[number], string> = {
  armed: 'Armed',
  violent: 'Violent',
  flight: 'Flight risk',
  mental_health: 'Mental health',
  officer_safety: 'Officer safety',
  medical: 'Medical',
};

/** A deputy's request for a warrant, from submitted to issued (or not). */
export const WARRANT_REQUEST_STATUS = ['submitted', 'in_review', 'needs_info', 'sent_to_judge', 'issued', 'denied', 'withdrawn'] as const;
export type WarrantRequestStatus = (typeof WARRANT_REQUEST_STATUS)[number];
export const WARRANT_REQUEST_STATUS_LABEL: Record<WarrantRequestStatus, string> = {
  submitted: 'Submitted',
  in_review: 'Being reviewed',
  needs_info: 'Needs more information',
  sent_to_judge: 'With the judge',
  issued: 'Issued',
  denied: 'Denied',
  withdrawn: 'Withdrawn',
};
/** Where a request can go next (and who moves it: W = warrants office, J = judge, D = requesting deputy). */
export const WARRANT_REQUEST_FLOW: Record<WarrantRequestStatus, Partial<Record<WarrantRequestStatus, 'W' | 'J' | 'D' | 'WJ'>>> = {
  submitted: { in_review: 'W', needs_info: 'W', sent_to_judge: 'W', denied: 'W', withdrawn: 'D' },
  in_review: { needs_info: 'W', sent_to_judge: 'W', denied: 'W', issued: 'W', withdrawn: 'D' },
  needs_info: { submitted: 'D', withdrawn: 'D', denied: 'W' },
  sent_to_judge: { issued: 'WJ', denied: 'WJ', needs_info: 'WJ', withdrawn: 'D' },
  issued: {},
  denied: {},
  withdrawn: {},
};

// ─── Tow reports ────────────────────────────────────────────────────────────
export const TOW_REASONS = ['accident', 'arrest', 'abandoned', 'disabled', 'parking', 'unlicensed', 'evidence', 'stolen_recovered', 'safekeeping', 'other'] as const;
export type TowReason = (typeof TOW_REASONS)[number];
export const TOW_REASON_LABEL: Record<TowReason, string> = {
  accident: 'Collision',
  arrest: 'Driver arrested',
  abandoned: 'Abandoned',
  disabled: 'Disabled / hazard in the road',
  parking: 'Illegally parked / blocking',
  unlicensed: 'No license, insurance or registration',
  evidence: 'Evidence / search warrant',
  stolen_recovered: 'Stolen, recovered',
  safekeeping: 'Safekeeping',
  other: 'Other',
};
export const TOW_STATUS = ['stored', 'released', 'disposed'] as const;
export type TowStatus = (typeof TOW_STATUS)[number];
export const TOW_STATUS_LABEL: Record<TowStatus, string> = { stored: 'At the tow lot', released: 'Released', disposed: 'Disposed' };
/** Reasons that put a police hold on the vehicle by default (it can't be released until a supervisor clears the hold). */
export const TOW_HOLD_REASONS: readonly TowReason[] = ['evidence', 'stolen_recovered'];

// ─── Tickets ────────────────────────────────────────────────────────────────
export const TICKET_KINDS = ['citation', 'warning'] as const;
export type TicketKind = (typeof TICKET_KINDS)[number];
export const TICKET_KIND_LABEL: Record<TicketKind, string> = { citation: 'Citation', warning: 'Written warning' };
/** Quick picks for the most common traffic violations (the deputy can type any other). */
export const COMMON_VIOLATIONS: { statute: string; description: string }[] = [
  { statute: '56-5-1520', description: 'Speeding' },
  { statute: '56-5-6520', description: 'Seat belt violation' },
  { statute: '56-1-460', description: 'Driving under suspension' },
  { statute: '56-5-2930', description: 'Driving under the influence' },
  { statute: '61-4-110', description: 'Open container (beer or wine) in a vehicle' },
];
